import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Folder } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Portfolio = () => {
  const [portfolioItems, setPortfolioItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPortfolio();
  }, []);

  const fetchPortfolio = async () => {
    try {
      const response = await axios.get(`${API}/portfolio`);
      setPortfolioItems(response.data);
    } catch (error) {
      console.error('Error fetching portfolio:', error);
    } finally {
      setLoading(false);
    }
  };

  // Placeholder data when database is empty
  const placeholderItems = [
    {
      id: '1',
      title: 'Brand Identity Design',
      description: 'Complete brand identity including logo, colors, and guidelines',
      category: 'Graphic Design',
      image_url: 'https://images.pexels.com/photos/16307279/pexels-photo-16307279.jpeg',
    },
    {
      id: '2',
      title: 'Business Strategy Overhaul',
      description: 'Market positioning and operational workflow optimization',
      category: 'Consultancy',
      image_url: 'https://images.pexels.com/photos/7654185/pexels-photo-7654185.jpeg',
    },
    {
      id: '3',
      title: 'Social Media Campaign',
      description: 'Multi-platform content strategy and execution',
      category: 'Digital Content',
      image_url: 'https://images.unsplash.com/photo-1724862936518-ae7fcfc052c1',
    },
  ];

  const displayItems = portfolioItems.length > 0 ? portfolioItems : placeholderItems;

  return (
    <div className="bg-eclipse-bg">
      {/* Hero Section */}
      <section
        className="relative py-24 md:py-32 overflow-hidden"
        style={{
          background:
            'radial-gradient(circle at top right, rgba(0, 217, 255, 0.15), transparent 40%), radial-gradient(circle at bottom left, rgba(255, 140, 66, 0.1), transparent 40%)',
        }}
        data-testid="portfolio-hero-section"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center max-w-4xl mx-auto">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-cyan mb-4">
              Our Work
            </p>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-none mb-6">
              Portfolio &{' '}
              <span className="bg-gradient-to-r from-brand-cyan to-brand-orange bg-clip-text text-transparent">
                Case Studies
              </span>
            </h1>
            <p className="text-lg lg:text-xl text-eclipse-secondary leading-relaxed">
              Explore our recent projects and see how we've helped businesses stand out.
            </p>
          </div>
        </div>
      </section>

      {/* Portfolio Grid */}
      <section className="py-24 md:py-32" data-testid="portfolio-grid-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand-cyan"></div>
              <p className="text-eclipse-secondary mt-4">Loading portfolio...</p>
            </div>
          ) : displayItems.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {displayItems.map((item, index) => (
                <div
                  key={item.id}
                  className="group bg-eclipse-surface border border-white/5 rounded-2xl overflow-hidden hover:-translate-y-1 hover:border-brand-cyan/50 hover:shadow-[0_0_30px_rgba(0,217,255,0.1)] transition-all duration-300"
                  data-testid={`portfolio-item-${index + 1}`}
                >
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={item.image_url}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-eclipse-bg to-transparent opacity-60"></div>
                  </div>
                  <div className="p-6">
                    <p className="text-xs font-bold uppercase tracking-wider text-brand-cyan mb-2">
                      {item.category}
                    </p>
                    <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                    <p className="text-eclipse-secondary text-sm leading-relaxed">
                      {item.description}
                    </p>
                    {item.case_study_url && (
                      <a
                        href={item.case_study_url}
                        className="inline-flex items-center gap-2 text-brand-cyan hover:text-brand-orange transition-colors mt-4 text-sm font-medium"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View Case Study
                        <ArrowRight size={16} />
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20" data-testid="portfolio-empty-state">
              <div className="inline-flex p-6 bg-eclipse-surface rounded-full mb-6">
                <Folder className="w-12 h-12 text-brand-cyan" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Portfolio Coming Soon</h3>
              <p className="text-eclipse-secondary max-w-md mx-auto">
                We're currently building our portfolio showcase. Check back soon to see our amazing
                work!
              </p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section
        className="py-24 md:py-32 relative overflow-hidden"
        style={{
          background:
            'radial-gradient(circle at center, rgba(0, 217, 255, 0.1), transparent 50%)',
        }}
        data-testid="portfolio-cta-section"
      >
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-6">
            Want to See Your Project Here?
          </h2>
          <p className="text-eclipse-secondary text-lg leading-relaxed mb-8">
            Let's create something exceptional together. Start your project with EclipseProfessionals
            today.
          </p>
          <Link
            to="/contact"
            data-testid="portfolio-contact-cta"
            className="inline-flex items-center gap-2 bg-brand-cyan text-eclipse-bg font-bold px-10 py-5 rounded-full hover:bg-white hover:scale-105 transition-all duration-300 text-lg"
          >
            Get Started
            <ArrowRight size={24} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Portfolio;